import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import clsx from 'clsx';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { Button, Types } from '@marriott/mi-ui-library';
import { StyledTextEditor } from './TextEditor.styles';
import { TextEditorProps, TextAlignOptions, FontStyleOptions } from './TextEditor.types';
import {
  BoldIcon,
  UnderlineIcon,
  ItalicIcon,
  LeftAlignIcon,
  CenterAlignIcon,
  RightAlignIcon,
  FontStyleIcon,
  LinkIcon,
  UnlinkIcon,
} from './icons';
import { ToolbarItem } from './ToolbarItem';
import { ToolbarItemMode } from './ToolbarItem/ToolbarItem.types';
import { TextFormField } from '../../../molecules';

const iconMap: Record<string, React.ReactNode> = {
  left: <LeftAlignIcon />,
  center: <CenterAlignIcon />,
  right: <RightAlignIcon />,
};

export const TextEditor: FC<TextEditorProps> = ({
  placeholder,
  maxLength,
  content = '',
  ariaLabel,
  boldAriaLabel,
  underlineAriaLabel,
  italicAriaLabel,
  toolbarOptions,
  onChange,
}) => {
  const { alignOptions, fontStyleOptions, linkOptions } = toolbarOptions;

  const [linkUrl, setLinkUrl] = useState('');
  const [, forceRender] = useState({});

  const isAdditionalFeaturesEnabled = false;

  const editorRef = useRef<HTMLDivElement>(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 4],
        },
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right'],
      }),
      Link.configure({
        openOnClick: false,
        autolink: false,
        linkOnPaste: true,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
    ],
    immediatelyRender: false,
    content,
    onUpdate({ editor }) {
      onChange(editor.getHTML(), editor.getText());
    },
    editorProps: {
      handleClick(_view, _pos, event) {
        const el = event.target as HTMLElement;
        if (el?.closest('a')) {
          event.preventDefault();
          return true;
        }
        return false;
      },

      handleTextInput(view, _from, _to, text) {
        const len = view.state.doc.textContent.length;
        if (maxLength && len + text.length > maxLength) {
          return true;
        }
        return false;
      },

      handlePaste(view, event) {
        if (!maxLength) return false;

        const clipboardText = event.clipboardData?.getData('text/plain') ?? '';
        if (!clipboardText) return false;

        const currentLength = view.state.doc.textContent.length;
        if (currentLength + clipboardText.length > maxLength) {
          return true;
        }
        return false;
      },
    },
  }) as Editor;

  useEffect(() => {
    if (!editor) return;

    const update = () => forceRender({});

    editor.on('selectionUpdate', update);
    editor.on('transaction', update);

    return () => {
      editor.off('selectionUpdate', update);
      editor.off('transaction', update);
    };
  }, [editor]);

  useEffect(() => {
    editor?.commands.setContent(content);
    if (editorRef.current) {
      const contentBox = editorRef.current.querySelector('.ProseMirror');
      contentBox?.setAttribute('role', 'textbox');
      contentBox?.setAttribute('aria-label', ariaLabel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, editor]);

  const toggleBold = useCallback(() => {
    editor.chain().focus().toggleBold().run();
  }, [editor]);

  const toggleUnderline = useCallback(() => {
    editor.chain().focus().toggleUnderline().run();
  }, [editor]);

  const toggleItalic = useCallback(() => {
    editor.chain().focus().toggleItalic().run();
  }, [editor]);

  const handleAlignmentSelect = useCallback(
    (key: string) => {
      const align = key as TextAlignOptions;
      editor.chain().focus().setTextAlign(align).run();
    },
    [editor]
  );

  const handleHeadingSelect = useCallback(
    (key: string) => {
      const selectedKey = key as FontStyleOptions;
      const chain = editor.chain().focus();

      if (selectedKey === FontStyleOptions.PARAGRAPH) {
        chain?.setParagraph().run();
        return;
      }

      const level = Number(selectedKey.substring(1)) as 2 | 4;
      chain?.toggleHeading({ level }).run();
    },
    [editor]
  );

  const handleLinkDialogOpen = useCallback(() => {
    const current = editor.getAttributes('link')?.['href'] ?? '';
    setLinkUrl(current);
  }, [editor]);

  const handleLinkDialogClose = useCallback(() => {
    setLinkUrl('');
  }, []);

  const submitInsertLink = useCallback(
    (close: () => void) => {
      if (!linkUrl) {
        close();
        return;
      }

      const { from, to } = editor.state.selection;
      const chain = editor.chain().focus();

      if (from === to) {
        chain.insertContent(linkUrl);
        chain.setTextSelection({ from, to: from + linkUrl.length });
      }
      chain.extendMarkRange('link').setLink({ href: linkUrl }).run();
      setLinkUrl('');
      close();
    },
    [editor, linkUrl]
  );

  const handleUnlink = () => {
    editor.chain().focus().unsetLink().run();
  };

  const textAlignOptions = alignOptions.map(option => ({
    ...option,
    icon: iconMap[option.key],
  }));

  const linkDialog = (close: () => void) => (
    <div>
      <h4 className="link-dialog-title">{linkOptions?.title}</h4>
      <div className="link-dialog-input">
        <TextFormField
          label={linkOptions?.pathLabel}
          type="text"
          placeholder={linkOptions?.placeholder}
          value={linkUrl}
          ariaLabel={linkOptions?.ariaLabel}
          onChange={e => setLinkUrl(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              submitInsertLink(close);
            }
          }}
          testId="tiptap-link-input"
          className="m-input-field"
        />
      </div>
      <div className="link-dialog-actions">
        <Button buttonCopy={linkOptions?.cancelLabel} className="m-button-secondary mr-2" callback={close} />
        <Button
          buttonCopy={linkOptions?.submitLabel}
          className="m-button-primary"
          callback={() => submitInsertLink(close)}
        />
      </div>
    </div>
  );

  if (!editor) {
    return null;
  }

  return (
    <StyledTextEditor ref={editorRef}>
      <div className="d-flex align-items-center p-2 menu">
        <Button
          type={Types.ButtonTypeVariation.Button}
          className={clsx('px-2 py-1 menu-item', editor.isActive('bold') && 'is-active')}
          ariaLabel={boldAriaLabel}
          callback={toggleBold}
        >
          <BoldIcon />
        </Button>
        <Button
          type={Types.ButtonTypeVariation.Button}
          className={clsx('px-2 py-1 menu-item', editor.isActive('underline') && 'is-active')}
          ariaLabel={underlineAriaLabel}
          callback={toggleUnderline}
        >
          <UnderlineIcon />
        </Button>
        <Button
          type={Types.ButtonTypeVariation.Button}
          className={clsx('px-2 py-1 menu-item', editor.isActive('italic') && 'is-active')}
          ariaLabel={italicAriaLabel}
          callback={toggleItalic}
        >
          <ItalicIcon />
        </Button>
        {isAdditionalFeaturesEnabled && (
          <>
            <ToolbarItem
              id="alignment"
              ariaLabel="Text Alignment"
              mode={ToolbarItemMode.DROPDOWN}
              icon={<LeftAlignIcon />}
              items={textAlignOptions}
              selectedKey={
                editor?.isActive({ textAlign: TextAlignOptions.CENTER })
                  ? TextAlignOptions.CENTER
                  : editor?.isActive({ textAlign: TextAlignOptions.RIGHT })
                  ? TextAlignOptions.RIGHT
                  : TextAlignOptions.LEFT
              }
              onSelect={handleAlignmentSelect}
              className="ms-2 menu-item"
            />
            <ToolbarItem
              id="heading"
              ariaLabel="Heading"
              mode={ToolbarItemMode.DROPDOWN}
              icon={<FontStyleIcon />}
              items={fontStyleOptions}
              selectedKey={
                editor?.isActive('heading', { level: 2 })
                  ? FontStyleOptions.HEADING2
                  : editor?.isActive('heading', { level: 4 })
                  ? FontStyleOptions.HEADING4
                  : FontStyleOptions.PARAGRAPH
              }
              onSelect={handleHeadingSelect}
              className="ms-2 menu-item"
            />
            <ToolbarItem
              id="link"
              ariaLabel="Link"
              mode={ToolbarItemMode.PANEL}
              icon={<LinkIcon />}
              onOpen={handleLinkDialogOpen}
              onClose={handleLinkDialogClose}
              className="ms-2 menu-item"
              renderPanel={linkDialog}
            />
            <Button
              type={Types.ButtonTypeVariation.Button}
              className={'px-2 py-1 menu-item'}
              ariaLabel={'Remove Link'}
              callback={handleUnlink}
              isDisabled={!editor.isActive('link')}
            >
              <UnlinkIcon />
            </Button>
          </>
        )}
      </div>
      {placeholder && !editor.getText().length ? <div className="placeholder">{placeholder}</div> : null}
      <EditorContent editor={editor} />
    </StyledTextEditor>
  );
};
